import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { paths } from '@/app/router/paths';
import {
  ChartDialog,
  CreateChartDialog,
} from '@/features/charts/components/chart-dialog';
import { ChartFromConfig } from '@/features/charts/components/chart-from-config';
import { useChartConfigsForProject } from '@/features/charts/hooks/use-chart-configs-for-project';
import { NavigationButton } from '@/features/projects/project-details/components/navigation-button';
import PlusIcon from '@/shared/assets/plus.svg?react';
import { PlaceholderButton } from '@/shared/components/actions/placeholder-button';
import { LoadingIndicator } from '@/shared/components/loading-indicator';
import { NoEntriesCardPlaceholder } from '@/shared/components/placeholders/no-entries-card-placeholder';
import { Section } from '@/shared/components/section/section';
import {
  SectionHeader,
  SectionTitle,
  SectionActions,
} from '@/shared/components/section/section-header';
import { Button } from '@/shared/components/ui/button';
import { ProjectType } from '@/shared/types/api';

interface ProjectDetailsChartsProps {
  projectId: number;
  projectType: ProjectType;
}

export const ProjectDetailsCharts = ({
  projectId,
  projectType,
}: ProjectDetailsChartsProps) => {
  const { t } = useTranslation('project', { keyPrefix: 'ChartsSection' });

  const {
    data: charts,
    isLoading,
    isError,
  } = useChartConfigsForProject({ id: projectId, projectType });

  const navigate = useNavigate();

  const addChartTrigger = (
    <Button variant={'greenGradient'} btnType="secondary">
      <PlusIcon />
      {t('create_chart_button_label')}
    </Button>
  );

  return (
    <Section>
      <SectionHeader>
        <SectionTitle>{t('title')}</SectionTitle>
        <SectionActions>
          <CreateChartDialog
            projectId={projectId}
            projectType={projectType}
            trigger={addChartTrigger}
          />
          <NavigationButton
            onClick={() => {
              navigate(paths.insights.path);
            }}
          />
        </SectionActions>
      </SectionHeader>
      <div className="flex flex-row gap-8 overflow-x-auto">
        {isLoading ? (
          <LoadingIndicator />
        ) : isError || !charts ? (
          <div>{t('error_loading_charts')}</div>
        ) : charts.length == 0 ? (
          <NoEntriesCardPlaceholder
            title={t('no_charts_placeholder')}
            description={t('no_charts_placeholder_description')}
          >
            <ChartDialog
              projectId={projectId}
              projectType={projectType}
              mode="create"
              trigger={<PlaceholderButton />}
            />
          </NoEntriesCardPlaceholder>
        ) : (
          charts.map((chart, index) => (
            <ChartFromConfig
              key={index}
              config={chart}
              projectType={projectType}
              dimensions={{
                height: 'max-h-[500px]',
                width: 'w-auto',
                minWidth: 'min-w-[900px]',
              }}
              chartClassName="h-[298px]"
            />
          ))
        )}
      </div>
    </Section>
  );
};
